'use client';

import classnames from 'classnames';
import {
  StandingsRowHeader,
  MatchCentreStandingsTable,
  StandingsTableApiResponse,
  StandingsRow,
  positionFormatter,
} from '@stadionhq-v3/football-ui';
import { AblyComponent } from '@stadionhq-v3/ably';
import { TableHeadCell } from '@stadionhq-v3/base-ui';
import {
  ALIGNMENTS,
  STANDINGS_ROW_HEADERS,
  T_SHIRT_SIZES,
} from '@stadionhq-v3/core';
import { useCallback } from 'react';
import { useResizeObserver } from '@stadionhq-v3/hooks';
import { QA_MATCH_CENTRE_LEAGUE_TABLE } from '../../../testConfig/testIds';
import { MatchCentreLeagueTableProps } from './matchCentreLeagueTable.types';
import styles from './matchCentreLeagueTable.module.scss';

const MatchCentreLeagueTable = ({
  testId = QA_MATCH_CENTRE_LEAGUE_TABLE.COMPONENT,
  classNames = [],
  nativeProps = {},
  dictionary,
  actionCta,
  competition,
  competitionHeaderDescription,
  competitionDividers,
  imageProvider,
  expandCta,
  initialNumberOfRows,
  alternateRowsEnabled,
  fallbackCrestUrl,
  teamId,
  LEAGUE_TABLES_DATA,
  ...ablyProps
}: MatchCentreLeagueTableProps) => {
  const classes = classnames([styles.matchCentreLeagueTable], classNames);
  const CONTAINER_WIDTH_THRESHOLD = 380;

  const { matches, setNode } = useResizeObserver(CONTAINER_WIDTH_THRESHOLD);

  const ref = useCallback(
    (node: HTMLDivElement | null) => {
      setNode(node);
    },
    [setNode]
  );

  const headRowCells: TableHeadCell<StandingsRowHeader>[] = [
    {
      key: STANDINGS_ROW_HEADERS.POSITION,
      label: dictionary.standings.positionAbbreviated,
      size: T_SHIRT_SIZES.S,
      fontSize: T_SHIRT_SIZES.L,
    },
    {
      key: STANDINGS_ROW_HEADERS.CREST_URL,
      label: dictionary.standings.teamNameAbbreviated,
      size: T_SHIRT_SIZES.XS,
    },
    {
      key: STANDINGS_ROW_HEADERS.CLUB_SHORT_NAME,
      label: '',
      align: ALIGNMENTS.LEFT,
      size: T_SHIRT_SIZES.XL,
      fontSize: T_SHIRT_SIZES.L,
    },
    {
      key: STANDINGS_ROW_HEADERS.PLAYED,
      label: dictionary.standings.playedAbbreviated,
      size: T_SHIRT_SIZES.XS,
    },
    {
      key: STANDINGS_ROW_HEADERS.GOAL_DIFFERENCE,
      label: dictionary.standings.goalDifferenceAbbreviated,
      size: T_SHIRT_SIZES.XS,
    },
    {
      key: STANDINGS_ROW_HEADERS.POINTS,
      label: dictionary.standings.pointsAbbreviated,
      size: T_SHIRT_SIZES.XS,
    },
  ];

  const headRowCellsCondensed: TableHeadCell<StandingsRowHeader>[] = [
    {
      key: STANDINGS_ROW_HEADERS.POSITION,
      label: dictionary.standings.positionAbbreviated,
      size: T_SHIRT_SIZES.S,
      fontSize: T_SHIRT_SIZES.L,
    },
    {
      key: STANDINGS_ROW_HEADERS.CLUB_SHORT_NAME,
      label: dictionary.standings.teamNameAbbreviated,
      align: ALIGNMENTS.LEFT,
      size: T_SHIRT_SIZES.XL,
      fontSize: T_SHIRT_SIZES.L,
    },
    {
      key: STANDINGS_ROW_HEADERS.PLAYED,
      label: dictionary.standings.playedAbbreviated,
      size: T_SHIRT_SIZES.XS,
    },
    {
      key: STANDINGS_ROW_HEADERS.GOAL_DIFFERENCE,
      label: dictionary.standings.goalDifferenceAbbreviated,
      size: T_SHIRT_SIZES.XS,
    },
    {
      key: STANDINGS_ROW_HEADERS.POINTS,
      label: dictionary.standings.pointsAbbreviated,
      size: T_SHIRT_SIZES.XS,
    },
  ];

  return ablyProps ? (
    <AblyComponent<StandingsTableApiResponse>
      {...ablyProps}
      {...nativeProps}
      storeKey="Tables"
      classNames={[classes]}
      testId={testId}
      render={(liveProps: StandingsTableApiResponse) => (
        <div ref={ref}>
          <MatchCentreStandingsTable
            standingsResponse={liveProps}
            competition={competition}
            headRowCells={matches ? headRowCells : headRowCellsCondensed}
            actionCta={actionCta}
            competitionHeaderDescription={
              liveProps.matchDay
                ? `${competitionHeaderDescription} ${liveProps.matchDay}`
                : undefined
            }
            condensed
            formatters={{
              position: (row: StandingsRow) => ({
                node: positionFormatter('row')(row),
              }),
            }}
            LEAGUE_TABLES_DATA={LEAGUE_TABLES_DATA}
            competitionDividers={competitionDividers}
            imageProvider={imageProvider}
            expandCta={expandCta}
            initialNumberOfRows={initialNumberOfRows}
            alternateRowsEnabled={alternateRowsEnabled}
            fallbackCrestUrl={fallbackCrestUrl}
            teamId={teamId}
          />
        </div>
      )}
    />
  ) : (
    <></>
  );
};

export default MatchCentreLeagueTable;
